import { createStore, Effect, Event } from 'effector';
import isEqual from 'react-fast-compare';

import { PAYMENT_PROCESSING_FAILURE_ID } from '@m/constants/ResponseErrorCode';
import { applyReducers } from '@m/effector-utils';
import { logout } from 'apps/customer/state/user/actions';
import { isRmeStatusInReview, isVisitActive } from 'apps/customer/utils/visit';

import {
  customerApplyValidation,
  customerRequestVehicle,
  customerSetTip,
  customerVisitPayment,
  fetchTipSettings,
  resetCustomerVisitStore,
  setCustomerVisits,
  setQRValidation,
  setCurrentVisitUserSession,
  setUserPendingValidation,
  setDoorOpenRequest,
  setPendingValidationTerms,
} from './actions';

import type { DoneHandler } from '@m/effector-utils';
import type { PastVisit, RateBaseV2, ValidationBase, Visit } from '@m/types/api';
import type { APIResponse } from '@m/utils/http';

type TipSettings = { suggestedTipAmounts: number[]; tippingEnabled: boolean | null };

type CustomerVisitState = {
  currentVisitUserSession?: { rate: RateBaseV2; validation: ValidationBase };
  // Keyed by visit id — a customer can have more than one open visit at once, and a
  // door-access grant applies to exactly one of them (or, when the grant arrives with
  // no visit context, to whichever active visits could plausibly show it).
  doorOpenRequest: Record<number, string | null>;
  // Tri-state like mostRecentVisit's other derived fields: null before the first
  // fetch resolves, then true/false based on whether any visit is currently active.
  hasActiveVisit: boolean | null;
  // Whether any visit is under manual-exit review -- routing consults this as a fallback
  // when hasActiveVisit is false, so an in-review-only customer still lands somewhere that
  // surfaces their status (Dashboard) instead of Home, which doesn't render it.
  hasInReviewVisit: boolean;
  initLoad: boolean;
  mostRecentVisit: Visit | null;
  paymentError?: {
    /**
     *  Custom message from BE.
     *  Currently used to determine if we need to route to payment page to re-add payment.
     */
    isCustomMessage: boolean;
    message: string;
  };
  // A door-access grant that arrived with no visit context (e.g. a deep link at app
  // load, before visits have even been fetched yet) -- re-applied to the active-visit
  // set whenever it changes (see setCustomerVisits) so it isn't silently dropped by
  // arriving before any visit exists to broadcast it to. Cleared once any
  // visit-scoped write/clear consumes it.
  pendingDoorAccessGrant: string | null;
  pendingValidationTerms: string | null;
  qrValidation?: string | null;
  requestVehicleError?: string;
  // Keyed by visit id — tip settings can legitimately differ across two concurrently
  // open valet visits (e.g. different sites).
  tipSettingsByVisit: Record<number, TipSettings>;
  unpaidVisits: PastVisit[];
  userPendingValidation?: { id: number; validationUuid?: string } | null;
  visitHistory: PastVisit[];
  visits: Visit[];
};

const initialState: CustomerVisitState = {
  visits: [],
  visitHistory: [],
  unpaidVisits: [],
  mostRecentVisit: null,
  // Do not rely on async store since async store can be reset
  initLoad: false, // Ensures that there was an attempt to load a visit
  hasActiveVisit: null,
  hasInReviewVisit: false,
  tipSettingsByVisit: {},
  doorOpenRequest: {},
  pendingDoorAccessGrant: null,
  pendingValidationTerms: null,
};

const store = createStore(initialState);

type Reducers = {
  customerApplyValidation: {
    action: Effect<Parameters<typeof customerApplyValidation>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof customerApplyValidation>[0], CustomerVisitState>;
  };
  customerRequestVehicle: {
    action: Effect<Parameters<typeof customerRequestVehicle>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof customerRequestVehicle>[0], CustomerVisitState>;
  };
  customerSetTip: {
    action: Effect<Parameters<typeof customerSetTip>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof customerSetTip>[0], CustomerVisitState>;
  };
  customerVisitPayment: {
    action: Effect<Parameters<typeof customerVisitPayment>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof customerVisitPayment>[0], CustomerVisitState>;
  };
  fetchTipSettings: {
    action: Effect<Parameters<typeof fetchTipSettings>[0], APIResponse>;
    done: DoneHandler<Parameters<typeof fetchTipSettings>[0], CustomerVisitState>;
  };
  resetCustomerVisitStore: {
    action: Event<Parameters<typeof resetCustomerVisitStore>[0]>;
    skipAsyncStore: boolean;
  };
  setCurrentVisitUserSession: {
    action: Event<{ rate: RateBaseV2; validation: ValidationBase }>;
    reducer: (
      state: CustomerVisitState,
      payload: { rate: RateBaseV2; validation: ValidationBase },
    ) => CustomerVisitState;
  };
  setCustomerVisits: {
    action: Event<Visit[]>;
    reducer: (state: CustomerVisitState, payload: Visit[]) => CustomerVisitState;
  };
  setDoorOpenRequest: {
    action: Event<{ access: string | null; visitId?: number }>;
    reducer: (
      state: CustomerVisitState,
      payload: { access: string | null; visitId?: number },
    ) => CustomerVisitState;
  };
  setPendingValidationTerms: {
    action: Event<string | null>;
    reducer: (state: CustomerVisitState, payload: string | null) => CustomerVisitState;
  };
  setQRValidation: {
    action: Event<string | null>;
    reducer: (state: CustomerVisitState, payload: string | null) => CustomerVisitState;
  };
  setUserPendingValidation: {
    action: Event<{ id: number; validationUuid?: string } | null>;
    reducer: (
      state: CustomerVisitState,
      payload: { id: number; validationUuid?: string } | null,
    ) => CustomerVisitState;
  };
};

export const reducers: Reducers = {
  setCustomerVisits: {
    action: setCustomerVisits,
    reducer: (state, visits) => {
      const isVisitsEqual = isEqual(visits, state.visits);
      const nextVisits = isVisitsEqual ? state.visits : visits;
      if (state.initLoad && isVisitsEqual) {
        return state;
      }
      // Re-apply a door-access grant that arrived before any visit existed to
      // broadcast it to (see setDoorOpenRequest) -- otherwise it's dropped the
      // moment it was set and never reaches the visit it was meant for.
      const doorOpenRequest = state.pendingDoorAccessGrant
        ? nextVisits
            .filter(isVisitActive)
            .reduce(
              (acc, visit) => ({ ...acc, [visit.id]: state.pendingDoorAccessGrant }),
              state.doorOpenRequest,
            )
        : state.doorOpenRequest;
      return {
        ...state,
        visits: nextVisits,
        mostRecentVisit: nextVisits[0] || null,
        hasActiveVisit: nextVisits.some(isVisitActive),
        hasInReviewVisit: nextVisits.some((visit) => isRmeStatusInReview(visit.rmeStatus)),
        doorOpenRequest,
        initLoad: true,
      };
    },
  },
  customerRequestVehicle: {
    action: customerRequestVehicle,
    done: (state, { result: { success, data } }) => {
      if (success) {
        const nextVisits = [...state.visits];
        const visitIndex = nextVisits.findIndex((visit) => visit.id === data.transaction.id);
        if (visitIndex > -1) {
          // TODO: Ensure the backend returns site and validation data in this route
          // otherwise the visit page blinks due to missing site and validation
          // ideally same response as fetchCustomerVisits
          // nextVisits[visitIndex] = data.transaction;
        }
        return {
          ...state,
          visits: nextVisits,
          mostRecentVisit: nextVisits[0] || null,
          requestVehicleError: undefined,
        };
      }
      return {
        ...state,
        // TODO: Handle this with the fail callback
        requestVehicleError: 'Unable to request vehicle. Please contact an attendant.',
      };
    },
  },
  // TODO: get visit in the response
  customerVisitPayment: {
    action: customerVisitPayment,
    done: (state, { result: { success, code, error } }) => {
      let paymentError;

      if (!success) {
        if (code && code.id === PAYMENT_PROCESSING_FAILURE_ID && error) {
          paymentError = {
            isCustomMessage: true,
            message: error,
          };
        } else {
          paymentError = {
            isCustomMessage: false,
            message: 'Payment failed. Please see an attendant.',
          };
        }
      }

      return {
        ...state,
        // TODO: Handle this with the fail callback
        paymentError,
      };
    },
  },
  customerApplyValidation: {
    action: customerApplyValidation,
    done: (state, { result: { success, data } }) => {
      if (success) {
        const { transaction, validation } = data;
        const nextVisits = [...state.visits];
        const visitIndex = nextVisits.findIndex((visit) => visit.id === transaction.id);
        if (visitIndex > -1) {
          // Update visit. Response only passes a partial transaction
          nextVisits[visitIndex] = {
            ...nextVisits[visitIndex],
            ...transaction,
            validation,
          };
        }
        return {
          ...state,
          visits: nextVisits,
          mostRecentVisit: nextVisits[0] || null,
        };
      }
      return state;
    },
  },
  setQRValidation: {
    action: setQRValidation,
    reducer: (state, validationUUID) => ({
      ...state,
      qrValidation: validationUUID,
    }),
  },
  setCurrentVisitUserSession: {
    action: setCurrentVisitUserSession,
    reducer: (state, { validation, rate }) => ({
      ...state,
      currentVisitUserSession: { validation, rate },
    }),
  },

  customerSetTip: {
    action: customerSetTip,
    done: (state, { result: { success, data } }) => {
      if (success) {
        const nextVisits = [...state.visits];
        const { transaction } = data;

        const visitIndex = nextVisits.findIndex((visit) => visit.id === transaction.id);
        if (visitIndex > -1) {
          nextVisits[visitIndex] = {
            ...nextVisits[visitIndex],
            // Update curr visit object
            ...transaction,
          };
        }
        return {
          ...state,
          visits: nextVisits,
          mostRecentVisit: nextVisits[0] || null,
        };
      }
      return state;
    },
  },
  fetchTipSettings: {
    action: fetchTipSettings,
    done: (state, { params: { visitId }, result: { success, data } }) => ({
      ...state,
      tipSettingsByVisit: {
        ...state.tipSettingsByVisit,
        [visitId]: {
          tippingEnabled: success ? data.tippingEnabled : null,
          suggestedTipAmounts: success ? data.suggestedTipAmounts : [],
        },
      },
    }),
  },
  resetCustomerVisitStore: {
    action: resetCustomerVisitStore,
    skipAsyncStore: true,
  },
  setDoorOpenRequest: {
    action: setDoorOpenRequest,
    reducer: (state, { access, visitId }) => {
      if (visitId !== undefined) {
        // A visit-scoped write/clear means this grant has been consumed (or was
        // never a broadcast in the first place) -- drop any pending grant so it
        // can't later get misapplied to an unrelated visit that becomes active.
        return {
          ...state,
          doorOpenRequest: { ...state.doorOpenRequest, [visitId]: access },
          pendingDoorAccessGrant: null,
        };
      }
      // No visit id available (e.g. a deep-link door-access grant) — broadcast to
      // every currently active visit. Door availability is already site-scoped, so
      // only the visit actually at that door's site will ever render UI for it.
      // Remembered as pendingDoorAccessGrant too, in case no visit is active yet --
      // see setCustomerVisits, which re-applies it once one arrives.
      const nextDoorOpenRequest = { ...state.doorOpenRequest };
      state.visits.filter(isVisitActive).forEach((visit) => {
        nextDoorOpenRequest[visit.id] = access;
      });
      return { ...state, doorOpenRequest: nextDoorOpenRequest, pendingDoorAccessGrant: access };
    },
  },
  setPendingValidationTerms: {
    action: setPendingValidationTerms,
    reducer: (state, pendingValidationTerms) => ({
      ...state,
      pendingValidationTerms,
    }),
  },
  setUserPendingValidation: {
    action: setUserPendingValidation,
    reducer: (state, userPendingValidation) => ({
      ...state,
      userPendingValidation,
    }),
  },
};

const asyncResetReducers = [logout.done, resetCustomerVisitStore];
store.reset(logout.done);

export default applyReducers({ storeName: 'customer/visit', store, reducers, asyncResetReducers });
